import {
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  Numbers as NumbersIcon,
  TableChart as TableChartIcon,
} from "@mui/icons-material";
import {
  Box,
  Card,
  CardContent,
  Dialog,
  IconButton,
  ToggleButton,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { exportChartDataToExcel } from "../../utils/chartExport";

const getFullscreenChartHeight = () =>
  typeof window !== "undefined" ? Math.max(480, window.innerHeight - 160) : 600;

const ChartCard = ({
  title,
  subtitle,
  children,
  height = 320,
  isRTL = false,
  exportCategories = [],
  exportSeries = [],
}) => {
  const { t, i18n } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [showLabels, setShowLabels] = useState(false);

  const toggleExpanded = useCallback(() => {
    setExpanded((prev) => !prev);
  }, []);

  const canExport =
    Array.isArray(exportCategories) &&
    exportCategories.length > 0 &&
    Array.isArray(exportSeries) &&
    exportSeries.length > 0;

  const handleExport = () => {
    if (!canExport) return;
    exportChartDataToExcel({
      title,
      categories: exportCategories,
      series: exportSeries,
      lang: i18n.language,
    });
  };

  const normalChartHeight = Math.max(200, height - 80);
  const layoutHeight = expanded ? getFullscreenChartHeight() : normalChartHeight;

  const toolbar = (
    <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0, gap: 0.5 }}>
      <Tooltip
        title={showLabels ? t("reports.hideChartValues") : t("reports.showChartValues")}
      >
        <ToggleButton
          value="labels"
          size="small"
          selected={showLabels}
          onChange={() => setShowLabels((prev) => !prev)}
          aria-label={t("reports.showChartValues")}
          sx={{ px: 0.75, py: 0.5 }}
        >
          <NumbersIcon fontSize="small" />
        </ToggleButton>
      </Tooltip>
      <Tooltip title={t("reports.exportChartExcel")}>
        <span>
          <IconButton
            size="small"
            onClick={handleExport}
            disabled={!canExport}
            aria-label={t("reports.exportChartExcel")}
          >
            <TableChartIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title={expanded ? t("reports.collapseChart") : t("reports.expandChart")}>
        <IconButton
          size="small"
          onClick={toggleExpanded}
          aria-label={expanded ? t("reports.collapseChart") : t("reports.expandChart")}
        >
          {expanded ? <FullscreenExitIcon /> : <FullscreenIcon />}
        </IconButton>
      </Tooltip>
    </Box>
  );

  const header = (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 1,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {toolbar}
    </Box>
  );

  const chartArea = (chartHeight) => (
    <Box
      dir="ltr"
      sx={{
        flex: 1,
        minHeight: chartHeight,
        width: "100%",
        overflow: "visible",
      }}
    >
      {typeof children === "function"
        ? children(chartHeight, expanded, showLabels)
        : children}
    </Box>
  );

  return (
    <>
      <Card
        dir={isRTL ? "rtl" : "ltr"}
        sx={{
          height: "100%",
          borderRadius: 2,
          boxShadow: 1,
          overflow: "visible",
        }}
      >
        <CardContent
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            pb: 1,
          }}
        >
          {header}
          {chartArea(normalChartHeight)}
        </CardContent>
      </Card>

      <Dialog
        fullScreen
        open={expanded}
        onClose={toggleExpanded}
        dir={isRTL ? "rtl" : "ltr"}
      >
        <Box
          sx={{
            p: 2,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            boxSizing: "border-box",
          }}
        >
          {header}
          {chartArea(layoutHeight)}
        </Box>
      </Dialog>
    </>
  );
};

export default ChartCard;
